<template>
   <Modal
      v-model="model"
      v-model:tab="modalTab"
      size="lg"
      :extra="organizationId"
      :tabs="[
         $t('common.details'),
         $t('common.dashboard-users'),
         {
            title: $t('common.points-management'),
            badge:
               !!organization.data.value?.[0].organizationpoints?.[0]?.is_updated_by_office &&
               !organization.data.value?.[0].organizationpoints?.[0]?.is_updated_by_organization,
         },
         {
            title: $t('common.stripe-account'),
            badge: !isStripeActive,
         },
         $t('common.events'),
      ]"
      :disabled-tabs="[false, false, isOffice, isOffice, false]"
      :title="organization.data.value?.[0].translated_name || ''"
      :loading="organization.isLoading.value"
      :description="$t('web.components.organization-modal.description')"
      :confirm-button="[
         {
            text: $t('common.save'),
            action: organizationDetailsRef?.action,
            loading: organizationDetailsRef?.loading,
            disabled: organizationDetailsRef?.disabled,
         },
         {
            text: $t('common.add'),
            action: organizationUsersRef?.action,
            loading: organizationUsersRef?.loading,
            disabled: organizationUsersRef?.disabled,
            icon: 'plus',
         },
         {
            text: $t('common.save'),
            action: organizationPointsRef?.action,
            loading: organizationPointsRef?.loading,
            disabled: !!organizationPointsRef?.disabled,
         },
         {
            text: $t('common.go-to-stripe'),
            action: organizationStripeRef?.action,
            loading: organizationStripeRef?.loading,
            disabled: organizationStripeRef?.disabled,
            icon: 'arrow-top-right-on-square',
         },
         undefined,
      ]"
   >
      <template #extra-header>
         <HighlightedText
            :type="isStripeActive ? 'success' : 'error'"
            :icon="isStripeActive ? 'check-circle' : 'ban'"
            :text="
               isStripeActive ? $t('common.app-selling-enabled') : $t('common.app-selling-disabled')
            "
         />
      </template>
      <template v-if="modalTab === 0">
         <OrganizationDetails
            v-if="organizationId"
            ref="organizationDetailsRef"
            :organization="organization.data.value?.[0]"
         />
      </template>
      <template v-if="modalTab === 1">
         <OrganizationUsers
            v-if="organizationId"
            ref="organizationUsersRef"
            :users="organization.data.value?.[0].user_organization"
         />
      </template>
      <template v-if="modalTab === 2">
         <OrganizationPoints
            v-if="organizationId"
            ref="organizationPointsRef"
            :organization-id="organizationId"
            type="organization"
         />
      </template>
      <template v-if="modalTab === 3">
         <OrganizationStripe
            v-if="organizationId"
            ref="organizationStripeRef"
            :stripeActive="isStripeActive"
         />
      </template>
      <template v-if="modalTab === 4">
         <OrganizationAdminlog v-if="organizationId" />
      </template>
   </Modal>
</template>

<script lang="ts" setup>
import type OrganizationDetails from '~/components/dashboard/tabs/OrganizationDetails.vue'
import type OrganizationPoints from '~/components/dashboard/tabs/OrganizationPoints.vue'
import type OrganizationStripe from '~/components/dashboard/tabs/OrganizationStripe.vue'
import type OrganizationUsers from '~/components/dashboard/tabs/OrganizationUsers.vue'
import { useOrganization } from '~/composables/api/queries/organization'

type Props = {}

const props = withDefaults(defineProps<Props>(), {})

const model = defineModel<boolean>()

const organizationId = useOrganizationId()
const organizationType = useOrganizationType()
const isOffice = computed(() => organizationType.value === 'OT_OFFICE')

const modalTab = ref<number>(0)
const organizationDetailsRef = ref<InstanceType<typeof OrganizationDetails>>()
const organizationPointsRef = ref<InstanceType<typeof OrganizationPoints>>()
const organizationUsersRef = ref<InstanceType<typeof OrganizationUsers>>()
const organizationStripeRef = ref<InstanceType<typeof OrganizationStripe>>()

const isStripeActive = computed(() => organization.data.value?.[0].is_stripe_active || false)

const organization = useOrganization(
   computed(() => ({
      id: organizationId.value,
      enabled: !!organizationId.value,
   }))
)
</script>
